import React from 'react';
import styled from 'styled-components';
import { useAppSelector, useTranslate } from '../../core/hooks';
import { useWidgetId } from '../../core/IdProvider';
import { tariffsState, adderResumeSelector, productSelector } from '../../features/product';
import { BodyLarge, BodyRegular } from '../../fonts/typography/Typography';
import { DarkTextColor } from '../../fonts/typography/TypographyColors';
import { convertIsoDateToShortDate, formatCents } from '../../common';
import { SellingMode } from '@src/core/buyerApiClient';
import useSelectedProductId from '@src/hooks/useSelectedProductId';

interface ProductAdderSummaryProps {
  hideTariffs?: boolean;
  hideResources?: boolean;
}

const ProductAdderSummary = ({ hideTariffs, hideResources }: ProductAdderSummaryProps) => {
  const productId = useSelectedProductId();
  const product = useAppSelector(productSelector(productId));
  const id = useWidgetId();
  const { selectedDate } = useAppSelector(tariffsState(id));
  const { selectedTariffs, selectedResources, resourcesPriceCents, tariffsPriceCents } = useAppSelector(
    adderResumeSelector(id),
  );
  const { i18n } = useTranslate();

  const tariffSum = selectedTariffs.reduce((sum, { selectedQuantity }) => sum + selectedQuantity, 0);

  return (
    <>
      <CombinedSummaryItens>
        <SummaryItem hidden={!selectedDate || product?.sellingMode === SellingMode.WithoutDate}>
          <SummaryLabel>{i18n('adder_selected_date')}</SummaryLabel>
          <SummaryText>{selectedDate ? convertIsoDateToShortDate(selectedDate) : null}</SummaryText>
        </SummaryItem>
        <SummaryItem hidden={hideTariffs || !selectedTariffs.length}>
          <SummaryLabel>{i18n('adder_selectedTariff')}</SummaryLabel>
          <SummaryText>
            {i18n('adder_quantity')}: {`${tariffSum} - ${formatCents(tariffsPriceCents)}`}
          </SummaryText>
        </SummaryItem>
      </CombinedSummaryItens>
      <SummaryItem hidden={hideResources || !selectedResources.length}>
        <SummaryLabel>{i18n('adder_selectedResources')}</SummaryLabel>
        <SummaryText>
          <span>{`${selectedResources.map((r) => r.resourceSlot.name).join(', ')}`}</span>
          {resourcesPriceCents > 0 && <span>{` - ${formatCents(resourcesPriceCents)}`}</span>}
        </SummaryText>
      </SummaryItem>
    </>
  );
};

const CombinedSummaryItens = styled.div`
  display: flex;
  gap: 16px 143px;
  flex-wrap: wrap;
`;

const SummaryItem = styled.div`
  margin-bottom: 16px;
`;

const SummaryLabel = styled.p`
  ${BodyLarge}
  ${DarkTextColor}
`;

const SummaryText = styled.p`
  ${BodyRegular}
  ${DarkTextColor}
`;

export default ProductAdderSummary;
